import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchGoogleAdsAccountSummary,
  fetchGoogleAdsOverview,
  fetchGoogleAdsCampaignsDirect,
  fetchGoogleAdsAdGroups,
  fetchGoogleAdsKeywords,
  fetchGoogleAdsAds,
  fetchGoogleAdsSegments,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Activity, Wallet, Gauge, Target, Users, FileText, Smartphone, Search as SearchIcon,
  AlertCircle, Loader2, DollarSign, MousePointerClick, Eye, TrendingUp,
} from "lucide-react";

const fmtNum = (n) => Number(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 0 });
const fmtCur = (n) => `$${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtPct = (n) => `${Number(n || 0).toFixed(2)}%`;

const SECTIONS = [
  { id: "summary",   label: "Resumen",      icon: Activity },
  { id: "budget",    label: "Saldo",        icon: Wallet },
  { id: "auction",   label: "Subasta",      icon: Gauge },
  { id: "campaigns", label: "Campañas",     icon: Target },
  { id: "adgroups",  label: "Grupos",       icon: Users },
  { id: "keywords",  label: "Keywords",     icon: SearchIcon },
  { id: "ads",       label: "Anuncios",     icon: FileText },
  { id: "segments",  label: "Dispositivos", icon: Smartphone },
];

export default function GoogleAdsFullSection({ customerId, range }) {
  const [section, setSection] = useState("summary");

  if (!customerId) {
    return <EmptyState message="Sin Google Ads Customer ID configurado." hint="Guardalo en Configurar compromiso → Google Ads Customer ID." />;
  }
  if (!range?.from || !range?.to) {
    return <EmptyState message="Seleccioná un período válido." />;
  }

  const body = { customer_id: customerId, start_date: range.from, end_date: range.to };

  return (
    <div className="space-y-4" data-testid="google-ads-full">
      <nav className="bg-white border border-slate-200 rounded-md p-1 flex flex-wrap gap-1" data-testid="gads-section-nav">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const active = section === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              data-testid={`gads-section-${s.id}`}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm font-medium transition-colors ${
                active ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {s.label}
            </button>
          );
        })}
      </nav>

      {section === "summary"   && <SummaryPanel body={body} customerId={customerId} />}
      {section === "budget"    && <BudgetPanel customerId={customerId} body={body} />}
      {section === "auction"   && <AuctionPanel body={body} />}
      {section === "campaigns" && <CampaignsPanel body={body} />}
      {section === "adgroups"  && <AdGroupsPanel body={body} />}
      {section === "keywords"  && <KeywordsPanel body={body} />}
      {section === "ads"       && <AdsPanel body={body} />}
      {section === "segments"  && <SegmentsPanel body={body} />}
    </div>
  );
}

/* ───────────────── 1. Resumen / KPIs ───────────────── */

function SummaryPanel({ body, customerId }) {
  const q = useQuery({
    queryKey: ["gads-overview", body],
    queryFn: () => fetchGoogleAdsOverview(body),
    staleTime: 5 * 60_000,
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBlock err={q.error} />;
  const o = q.data || {};
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="gads-summary-panel">
      <h3 className="font-display text-sm font-semibold text-slate-900 flex items-center gap-2"><Activity className="h-4 w-4 text-indigo-600" /> Rendimiento general</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Eye} label="Impresiones" value={fmtNum(o.impressions)} tone="indigo" />
        <Kpi icon={MousePointerClick} label="Clicks" value={fmtNum(o.clicks)} tone="sky" />
        <Kpi icon={Target} label="Conversiones" value={fmtNum(o.conversions)} tone="emerald" />
        <Kpi icon={DollarSign} label="Costo total" value={fmtCur(o.cost)} tone="amber" />
        <Kpi icon={TrendingUp} label="CTR" value={fmtPct(o.ctr)} tone="violet" />
        <Kpi icon={DollarSign} label="CPC promedio" value={fmtCur(o.avg_cpc)} tone="rose" />
        <Kpi icon={DollarSign} label="Costo/conversión" value={o.cost_per_conversion != null ? fmtCur(o.cost_per_conversion) : "—"} tone="slate" />
        <Kpi icon={TrendingUp} label="ROAS" value={o.roas != null ? `${o.roas.toFixed(2)}x` : "—"} tone="emerald" hint={o.conversions_value > 0 ? `Valor: ${fmtCur(o.conversions_value)}` : "Sin valor de conversión configurado"} />
      </div>
      <p className="text-[10px] text-slate-400 italic">Customer ID: <span className="font-mono">{customerId}</span></p>
    </section>
  );
}

/* ───────────────── 2. Saldo / Budget ───────────────── */

function BudgetPanel({ customerId, body }) {
  const q = useQuery({
    queryKey: ["gads-account-summary", customerId],
    queryFn: () => fetchGoogleAdsAccountSummary(customerId),
    staleTime: 5 * 60_000,
  });
  // Spend for the explicitly selected period (independent of MTD)
  const periodQ = useQuery({
    queryKey: ["gads-overview", body],
    queryFn: () => fetchGoogleAdsOverview(body),
    staleTime: 5 * 60_000,
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBlock err={q.error} />;
  const { customer = {}, budgets = [], month_to_date: mtd = {} } = q.data || {};
  const periodSpend = periodQ.data || {};
  const activeBudgets = budgets.filter((b) => b.status === "2" || b.status === "APPROVED");

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="gads-budget-panel">
      <div>
        <h3 className="font-display text-sm font-semibold text-slate-900 flex items-center gap-2"><Wallet className="h-4 w-4 text-indigo-600" /> Saldo y consumo</h3>
        <p className="text-[11px] text-slate-500 mt-1">Cuenta: <span className="font-semibold text-slate-700">{customer.name || customer.id}</span> · {customer.currency} · {customer.tz}</p>
      </div>

      <div>
        <h4 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Mes calendario en curso (MTD)</h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={DollarSign} label="Gasto del mes" value={fmtCur(mtd.cost)} tone="amber" hint={`${mtd.period_start || ""} → ${mtd.period_end || ""}`} />
          <Kpi icon={TrendingUp} label="Promedio diario" value={fmtCur(mtd.avg_daily)} tone="indigo" hint={`${mtd.days || 0} días corridos`} />
          <Kpi icon={Eye} label="Impresiones MTD" value={fmtNum(mtd.impressions)} tone="sky" />
          <Kpi icon={Target} label="Conversiones MTD" value={fmtNum(mtd.conversions)} tone="emerald" />
        </div>
      </div>

      <div>
        <h4 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Período seleccionado</h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={DollarSign} label="Gasto del período" value={fmtCur(periodSpend.cost)} tone="amber" hint={`${body.start_date} → ${body.end_date}`} />
          <Kpi icon={Eye} label="Impresiones" value={fmtNum(periodSpend.impressions)} tone="sky" />
          <Kpi icon={MousePointerClick} label="Clicks" value={fmtNum(periodSpend.clicks)} tone="indigo" />
          <Kpi icon={Target} label="Conversiones" value={fmtNum(periodSpend.conversions)} tone="emerald" />
        </div>
      </div>

      <div>
        <h4 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Presupuestos configurados (cuenta · histórico)</h4>
        {budgets.length === 0 ? (
          <div className="text-xs text-slate-500 italic bg-slate-50 border border-dashed border-slate-200 rounded-md p-4">
            Esta cuenta no tiene presupuestos cap configurados (post-pay). Se factura al final del mes lo gastado.
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                  <th className="px-3 py-2 font-semibold">ID / Nombre</th>
                  <th className="px-2 py-2 font-semibold text-right">Límite</th>
                  <th className="px-2 py-2 font-semibold text-right">Gastado</th>
                  <th className="px-2 py-2 font-semibold text-right">Restante</th>
                  <th className="px-3 py-2 font-semibold">Período</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {budgets.map((b) => {
                  const pct = b.limit ? (b.served / b.limit) * 100 : 0;
                  const isActive = activeBudgets.some((a) => a.id === b.id);
                  return (
                    <tr key={b.id} className={!isActive ? "opacity-60" : ""}>
                      <td className="px-3 py-2">
                        <div className="font-mono text-[10px] text-slate-500">{b.id}</div>
                        <div className="text-slate-800">{b.name || <span className="italic text-slate-400">(sin nombre)</span>}</div>
                      </td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums">{b.limit != null ? fmtCur(b.limit) : "—"}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-amber-700">{fmtCur(b.served)}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums">
                        {b.remaining != null ? (
                          <span className={b.remaining < 100 ? "text-rose-700 font-semibold" : "text-emerald-700 font-semibold"}>{fmtCur(b.remaining)}</span>
                        ) : <span className="text-slate-400">ilimitado</span>}
                        {b.limit ? <div className="text-[9px] text-slate-400 mt-0.5">{pct.toFixed(0)}% consumido</div> : null}
                      </td>
                      <td className="px-3 py-2 text-[10px] text-slate-500">
                        {b.start ? b.start.slice(0, 10) : "—"} → {b.end ? b.end.slice(0, 10) : "Sin fin"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

/* ───────────────── 3. Auction Insights ───────────────── */

function AuctionPanel({ body }) {
  const q = useQuery({
    queryKey: ["gads-overview", body],
    queryFn: () => fetchGoogleAdsOverview(body),
    staleTime: 5 * 60_000,
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBlock err={q.error} />;
  const is_ = q.data?.impression_share || {};
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="gads-auction-panel">
      <div>
        <h3 className="font-display text-sm font-semibold text-slate-900 flex items-center gap-2"><Gauge className="h-4 w-4 text-indigo-600" /> Métricas de subasta</h3>
        <p className="text-[11px] text-slate-500 mt-1">Posición relativa en la subasta de Google Ads · promedios ponderados por impresiones · solo campañas SEARCH</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Kpi icon={Eye} label="Search Impression Share" value={is_.search_is != null ? `${is_.search_is}%` : "—"} tone="indigo" hint="% de impresiones que ganaste vs las disponibles" />
        <Kpi icon={TrendingUp} label="Search Top IS" value={is_.search_top_is != null ? `${is_.search_top_is}%` : "—"} tone="sky" hint="Aparecés arriba de los resultados orgánicos" />
        <Kpi icon={TrendingUp} label="Absolute Top IS" value={is_.search_abs_top_is != null ? `${is_.search_abs_top_is}%` : "—"} tone="emerald" hint="Aparecés en la posición #1 de la SERP" />
      </div>
      <div>
        <h4 className="text-xs font-semibold text-slate-700 mt-2 mb-2">Impresiones perdidas</h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={Wallet} label="Perdido por presupuesto" value={is_.lost_budget != null ? `${is_.lost_budget}%` : "—"} tone={is_.lost_budget > 30 ? "rose" : "amber"} hint="Aumentá budget para capturarlas" />
          <Kpi icon={Gauge} label="Perdido por rank" value={is_.lost_rank != null ? `${is_.lost_rank}%` : "—"} tone={is_.lost_rank > 30 ? "rose" : "amber"} hint="Mejorá Quality Score y bid" />
          <Kpi icon={Wallet} label="Top perdido por budget" value={is_.lost_abs_top_budget != null ? `${is_.lost_abs_top_budget}%` : "—"} tone="violet" hint="Top position perdida por presupuesto" />
          <Kpi icon={Gauge} label="Top perdido por rank" value={is_.lost_abs_top_rank != null ? `${is_.lost_abs_top_rank}%` : "—"} tone="violet" hint="Top position perdida por bid/QS" />
        </div>
      </div>
      {is_.lost_budget != null && is_.lost_budget > 20 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Oportunidad: estás perdiendo {is_.lost_budget}% de impresiones por presupuesto.</div>
            <div className="mt-0.5">Aumentar el budget diario te haría capturar más tráfico calificado.</div>
          </div>
        </div>
      )}
      <p className="text-[10px] text-slate-400 italic">Overlap Rate / Position Above Rate vs competidores específicos no está disponible en la API pública. Para esos datos, exportá Auction Insights desde la UI de Google Ads.</p>
    </section>
  );
}

/* ───────────────── 4. Campaigns ───────────────── */

function CampaignsPanel({ body }) {
  const q = useQuery({
    queryKey: ["gads-campaigns", body],
    queryFn: () => fetchGoogleAdsCampaignsDirect(body),
    staleTime: 5 * 60_000,
  });
  return (
    <DataTable
      title="Campañas" icon={Target} testid="gads-campaigns-panel"
      query={q} rowsKey="rows" filterField="name"
      columns={[
        { k: "name",         label: "Campaña",       w: 220 },
        { k: "type",         label: "Tipo",          render: (v) => <Pill>{v.replace("CampaignType.","")}</Pill> },
        { k: "status",       label: "Estado",        render: (v) => <span className={`text-[10px] font-semibold ${v === "ENABLED" ? "text-emerald-700" : "text-slate-400"}`}>{v}</span> },
        { k: "budget_daily", label: "Budget/día",   align: "right", render: (v) => v ? fmtCur(v) : "—", cls: "text-indigo-700 font-semibold" },
        { k: "impressions",  label: "Impr.",         align: "right", render: fmtNum },
        { k: "clicks",       label: "Clicks",        align: "right", render: fmtNum, cls: "text-sky-700 font-semibold" },
        { k: "ctr",          label: "CTR",           align: "right", render: (v) => `${v}%` },
        { k: "avg_cpc",      label: "CPC",           align: "right", render: fmtCur },
        { k: "conversions",  label: "Conv.",         align: "right", render: fmtNum, cls: "text-emerald-700 font-semibold" },
        { k: "cost",         label: "Gasto",         align: "right", render: fmtCur, cls: "text-amber-700 font-semibold" },
        { k: "search_is",    label: "Search IS",     align: "right", render: (v) => v != null ? `${v}%` : "—", cls: "text-violet-700" },
        { k: "roas",         label: "ROAS",          align: "right", render: (v) => v != null ? `${v.toFixed(2)}x` : "—" },
      ]}
    />
  );
}

/* ───────────────── 5. Ad Groups ───────────────── */

function AdGroupsPanel({ body }) {
  const q = useQuery({
    queryKey: ["gads-adgroups", body],
    queryFn: () => fetchGoogleAdsAdGroups(body),
    staleTime: 5 * 60_000,
  });
  return (
    <DataTable
      title="Grupos de anuncios" icon={Users} testid="gads-adgroups-panel"
      query={q} rowsKey="rows" filterField="name"
      columns={[
        { k: "name",          label: "Grupo",       w: 200 },
        { k: "campaign_name", label: "Campaña",     w: 220, cls: "text-[10px] text-slate-500" },
        { k: "status",        label: "Estado",      render: (v) => <span className={`text-[10px] font-semibold ${v === "ENABLED" ? "text-emerald-700" : "text-slate-400"}`}>{v}</span> },
        { k: "impressions",   label: "Impr.",       align: "right", render: fmtNum },
        { k: "clicks",        label: "Clicks",      align: "right", render: fmtNum, cls: "text-sky-700 font-semibold" },
        { k: "ctr",           label: "CTR",         align: "right", render: (v) => `${v}%` },
        { k: "avg_cpc",       label: "CPC",         align: "right", render: fmtCur },
        { k: "conversions",   label: "Conv.",       align: "right", render: fmtNum, cls: "text-emerald-700 font-semibold" },
        { k: "cost",          label: "Gasto",       align: "right", render: fmtCur, cls: "text-amber-700 font-semibold" },
        { k: "roas",          label: "ROAS",        align: "right", render: (v) => v != null ? `${v.toFixed(2)}x` : "—" },
      ]}
    />
  );
}

/* ───────────────── 6. Keywords ───────────────── */

function KeywordsPanel({ body }) {
  const q = useQuery({
    queryKey: ["gads-keywords", body],
    queryFn: () => fetchGoogleAdsKeywords({ ...body, limit: 500 }),
    staleTime: 5 * 60_000,
  });
  const qsColor = (qs) => {
    if (qs == null) return "bg-slate-100 text-slate-500";
    if (qs >= 7) return "bg-emerald-100 text-emerald-700";
    if (qs >= 5) return "bg-amber-100 text-amber-700";
    return "bg-rose-100 text-rose-700";
  };
  return (
    <DataTable
      title="Keywords + Quality Score" icon={SearchIcon} testid="gads-keywords-panel"
      query={q} rowsKey="rows" filterField="keyword"
      columns={[
        { k: "keyword",      label: "Keyword",       w: 200 },
        { k: "match_type",   label: "Match",         render: (v) => <Pill>{v}</Pill> },
        { k: "quality_score", label: "QS",           align: "center", render: (v) => (
          <span className={`inline-flex items-center justify-center min-w-[28px] h-6 rounded-full font-mono text-xs font-bold ${qsColor(v)}`}>{v ?? "—"}</span>
        ) },
        { k: "campaign",     label: "Campaña",       w: 180, cls: "text-[10px] text-slate-500" },
        { k: "ad_group_name", label: "Grupo",        w: 140, cls: "text-[10px] text-slate-500" },
        { k: "status",       label: "Estado",        render: (v) => <span className={`text-[10px] font-semibold ${v === "ENABLED" ? "text-emerald-700" : "text-slate-400"}`}>{v}</span> },
        { k: "impressions",  label: "Impr.",         align: "right", render: fmtNum },
        { k: "clicks",       label: "Clicks",        align: "right", render: fmtNum, cls: "text-sky-700 font-semibold" },
        { k: "ctr",          label: "CTR",           align: "right", render: (v) => `${v}%` },
        { k: "avg_cpc",      label: "CPC",           align: "right", render: fmtCur },
        { k: "cost",         label: "Gasto",         align: "right", render: fmtCur, cls: "text-amber-700 font-semibold" },
        { k: "conversions",  label: "Conv.",         align: "right", render: fmtNum, cls: "text-emerald-700 font-semibold" },
      ]}
    />
  );
}

/* ───────────────── 7. Ads (copies) ───────────────── */

function AdsPanel({ body }) {
  const q = useQuery({
    queryKey: ["gads-ads", body],
    queryFn: () => fetchGoogleAdsAds({ ...body, limit: 200 }),
    staleTime: 5 * 60_000,
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBlock err={q.error} />;
  const ads = q.data?.rows || [];
  return (
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="gads-ads-panel">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <FileText className="h-4 w-4 text-indigo-600" />
        <h3 className="font-display text-sm font-semibold text-slate-900">Anuncios</h3>
        <span className="text-[10px] text-slate-400">{ads.length} anuncios</span>
      </div>
      {ads.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-400">Sin anuncios en el período.</div>
      ) : (
        <div className="divide-y divide-slate-100 max-h-[40rem] overflow-y-auto">
          {ads.map((ad) => (
            <div key={ad.ad_id} className="px-5 py-4 hover:bg-slate-50">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Pill>{ad.type}</Pill>
                <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                  ad.ad_strength === "EXCELLENT" || ad.ad_strength === "GOOD" ? "bg-emerald-100 text-emerald-700" :
                  ad.ad_strength === "AVERAGE" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"
                }`}>{ad.ad_strength}</span>
                <span className={`text-[9px] font-semibold ${ad.status === "ENABLED" ? "text-emerald-700" : "text-slate-400"}`}>{ad.status}</span>
                <span className="text-[10px] text-slate-500 ml-auto">{ad.campaign} · {ad.ad_group_name}</span>
              </div>
              {ad.headlines.length > 0 && (
                <div className="mb-2">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Headlines</div>
                  <div className="flex flex-wrap gap-1">
                    {ad.headlines.map((h, i) => <span key={i} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{h}</span>)}
                  </div>
                </div>
              )}
              {ad.descriptions.length > 0 && (
                <div className="mb-2">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Descripciones</div>
                  {ad.descriptions.map((d, i) => <p key={i} className="text-[11px] text-slate-600 leading-relaxed">{d}</p>)}
                </div>
              )}
              {ad.final_url && (
                <a href={ad.final_url} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-indigo-600 hover:underline">{ad.final_url}</a>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mt-3 text-[11px]">
                <Stat label="Impr." value={fmtNum(ad.impressions)} />
                <Stat label="Clicks" value={fmtNum(ad.clicks)} tone="sky" />
                <Stat label="CTR" value={`${ad.ctr}%`} />
                <Stat label="CPC" value={fmtCur(ad.avg_cpc)} />
                <Stat label="Gasto" value={fmtCur(ad.cost)} tone="amber" />
                <Stat label="Conv." value={fmtNum(ad.conversions)} tone="emerald" />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ───────────────── 8. Segments (Devices + Audiences) ───────────────── */

function SegmentsPanel({ body }) {
  const q = useQuery({
    queryKey: ["gads-segments", body],
    queryFn: () => fetchGoogleAdsSegments(body),
    staleTime: 5 * 60_000,
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBlock err={q.error} />;
  const devices = q.data?.devices || [];
  const audiences = q.data?.audiences || [];
  return (
    <div className="space-y-4">
      <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="gads-devices-panel">
        <h3 className="font-display text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3"><Smartphone className="h-4 w-4 text-indigo-600" /> Por dispositivo</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {devices.map((d) => (
            <div key={d.device} className="border border-slate-200 rounded-md p-3 bg-slate-50">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{d.device}</div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <Stat label="Impr." value={fmtNum(d.impressions)} />
                <Stat label="Clicks" value={fmtNum(d.clicks)} tone="sky" />
                <Stat label="CTR" value={`${d.ctr}%`} />
                <Stat label="CPC" value={fmtCur(d.avg_cpc)} />
                <Stat label="Gasto" value={fmtCur(d.cost)} tone="amber" />
                <Stat label="Conv." value={fmtNum(d.conversions)} tone="emerald" />
              </div>
            </div>
          ))}
          {devices.length === 0 && <div className="text-xs text-slate-400 italic col-span-full">Sin datos por dispositivo.</div>}
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="gads-audiences-panel">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="font-display text-sm font-semibold text-slate-900 flex items-center gap-2"><Users className="h-4 w-4 text-indigo-600" /> Audiencias</h3>
          <p className="text-[10px] text-slate-500 mt-0.5">Segmentos con bid adjustment activo · {audiences.length} segmentos</p>
        </div>
        {audiences.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-400">Sin segmentos de audiencia configurados en este período.</div>
        ) : (
          <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                  <th className="px-3 py-2">Audiencia ID</th>
                  <th className="px-2 py-2">Campaña / Grupo</th>
                  <th className="px-2 py-2 text-right">Impr.</th>
                  <th className="px-2 py-2 text-right">Clicks</th>
                  <th className="px-2 py-2 text-right">CTR</th>
                  <th className="px-2 py-2 text-right">CPC</th>
                  <th className="px-2 py-2 text-right">Conv.</th>
                  <th className="px-2 py-2 text-right">Gasto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {audiences.map((a, i) => (
                  <tr key={`${a.audience_id}-${i}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-600">{a.audience_id}</td>
                    <td className="px-2 py-2 text-[10px] text-slate-500"><div>{a.campaign}</div><div className="text-slate-400">{a.ad_group}</div></td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums">{fmtNum(a.impressions)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums text-sky-700 font-semibold">{fmtNum(a.clicks)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums">{a.ctr}%</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums">{fmtCur(a.avg_cpc)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums text-emerald-700 font-semibold">{fmtNum(a.conversions)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums text-amber-700">{fmtCur(a.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/* ───────────────── Shared sub-components ───────────────── */

function Kpi({ icon: Icon, label, value, tone = "slate", hint }) {
  const tones = {
    indigo: "from-indigo-50 to-white border-indigo-200 text-indigo-700",
    sky: "from-sky-50 to-white border-sky-200 text-sky-700",
    emerald: "from-emerald-50 to-white border-emerald-200 text-emerald-700",
    violet: "from-violet-50 to-white border-violet-200 text-violet-700",
    amber: "from-amber-50 to-white border-amber-200 text-amber-700",
    rose: "from-rose-50 to-white border-rose-200 text-rose-700",
    slate: "from-slate-50 to-white border-slate-200 text-slate-700",
  };
  return (
    <div className={`bg-gradient-to-b ${tones[tone]} border rounded-md p-3`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3.5 w-3.5" />
        <div className="text-[9px] uppercase tracking-wider opacity-90 font-semibold">{label}</div>
      </div>
      <div className="font-mono text-xl font-semibold tabular-nums text-slate-900">{value}</div>
      {hint && <div className="text-[9px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const colors = { sky: "text-sky-700", emerald: "text-emerald-700", amber: "text-amber-700", slate: "text-slate-700" };
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`font-mono font-semibold tabular-nums ${colors[tone]}`}>{value}</div>
    </div>
  );
}

function Pill({ children }) {
  return <span className="text-[9px] font-mono uppercase tracking-wider text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">{children}</span>;
}

function Loading() {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
      </div>
    </div>
  );
}

function ErrorBlock({ err }) {
  return (
    <div className="bg-rose-50 border border-rose-200 rounded-md p-4 text-sm text-rose-800 flex items-start gap-2">
      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold">No se pudo cargar</div>
        <div className="text-xs mt-1">{err?.response?.data?.detail || err?.message}</div>
      </div>
    </div>
  );
}

function EmptyState({ message, hint }) {
  return (
    <div className="bg-slate-50 border border-dashed border-slate-200 rounded-md p-10 text-center">
      <div className="text-sm text-slate-500">{message}</div>
      {hint && <div className="text-[11px] text-slate-400 mt-1.5">{hint}</div>}
    </div>
  );
}

function DataTable({ title, icon: Icon, testid, query, rowsKey, filterField, columns }) {
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const rows = query.data?.[rowsKey] || [];
  const filtered = useMemo(() => {
    let arr = filter.trim() ? rows.filter((r) => String(r[filterField] || "").toLowerCase().includes(filter.toLowerCase())) : [...rows];
    if (sortKey) {
      arr.sort((a, b) => {
        const av = a[sortKey], bv = b[sortKey];
        const c = typeof av === "string" ? String(av).localeCompare(String(bv)) : (Number(av || 0) - Number(bv || 0));
        return sortDir === "asc" ? c : -c;
      });
    }
    return arr;
  }, [rows, filter, sortKey, sortDir, filterField]);
  const toggleSort = (k) => { if (sortKey === k) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("desc"); } };
  return (
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        {Icon ? <Icon className="h-4 w-4 text-indigo-600" /> : null}
        <h3 className="font-display text-sm font-semibold text-slate-900">{title}</h3>
        <span className="text-[10px] text-slate-400">{rows.length} totales · mostrando {filtered.length}</span>
        <div className="relative ml-auto w-full sm:w-64">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input placeholder="Filtrar…" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 pl-9 bg-white text-xs" />
        </div>
      </div>
      {query.isLoading ? <Loading /> : query.error ? <ErrorBlock err={query.error} /> : rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-400">Sin datos en el período.</div>
      ) : (
        <div className="overflow-x-auto max-h-[40rem] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                {columns.map((c) => (
                  <th key={c.k} className={`px-2 py-2 ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : ""}`} style={c.w ? { minWidth: c.w } : undefined}>
                    <button onClick={() => toggleSort(c.k)} className={`font-semibold uppercase tracking-wider text-[10px] hover:text-slate-700 ${sortKey === c.k ? "text-indigo-700" : "text-slate-500"}`}>
                      {c.label}{sortKey === c.k ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r, i) => (
                <tr key={r.criterion_id || r.campaign_id || r.ad_group_id || r.ad_id || i} className="hover:bg-slate-50">
                  {columns.map((c) => {
                    const v = r[c.k];
                    const rendered = c.render ? c.render(v, r) : (v ?? "—");
                    return (
                      <td
                        key={c.k}
                        className={`px-2 py-2 ${c.align === "right" ? "text-right font-mono tabular-nums" : c.align === "center" ? "text-center" : ""} ${c.cls || ""} ${c.w ? "truncate max-w-[" + c.w + "px]" : ""}`}
                        title={c.w ? String(v ?? "") : undefined}
                        style={c.w ? { maxWidth: c.w } : undefined}
                      >
                        <div className={c.w ? "truncate" : ""}>{rendered}</div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
